/**
 * Conversation transcript panel for the Qualified Lead detail page.
 *
 * Renders the snapshot of `raw_messages` captured at qualification time. The
 * worker stores the same shape used by the legacy middleware: an array of
 * GHL message objects with `direction`, `body`, `dateAdded`, `messageType`.
 * We keep the rendering flexible because the schema is loose (the worker
 * upgrades over time and we don't want the dashboard to hard-fail on new
 * fields).
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { MessageSquare } from 'lucide-react'

interface RawMessage {
  readonly direction?: string
  readonly body?: string
  readonly dateAdded?: string
  readonly messageType?: string
  readonly senderName?: string | null
}

interface Props {
  readonly rawMessages: unknown[] | null
}

function asMessage(value: unknown): RawMessage | null {
  if (!value || typeof value !== 'object') return null
  const v = value as Record<string, unknown>
  return {
    direction: typeof v.direction === 'string' ? v.direction : undefined,
    body: typeof v.body === 'string' ? v.body : undefined,
    dateAdded: typeof v.dateAdded === 'string' ? v.dateAdded : undefined,
    messageType: typeof v.messageType === 'string' ? v.messageType : undefined,
    senderName: typeof v.senderName === 'string' ? v.senderName : null,
  }
}

export function ConversationTranscript({ rawMessages }: Props) {
  const messages = (rawMessages ?? []).map(asMessage).filter((m): m is RawMessage => m !== null)

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Conversation transcript
          <span className="ml-auto text-xs font-normal text-muted-foreground">
            {messages.length} {messages.length === 1 ? 'message' : 'messages'}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {messages.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No customer messages captured. Claude was given context only from the
            contact metadata.
          </p>
        ) : (
          <ul className="space-y-3">
            {messages.map((msg, i) => {
              const direction = msg.direction?.toLowerCase() ?? 'unknown'
              const isInbound = direction === 'inbound'
              const labelText = isInbound ? 'INBOUND' : direction === 'outbound' ? 'OUTBOUND' : direction.toUpperCase()
              return (
                <li
                  key={i}
                  className={`rounded-md border px-3 py-2 ${isInbound ? 'bg-blue-50/40 border-blue-100' : 'bg-muted/30 border-muted'}`}
                >
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide font-semibold">
                    <span
                      className={isInbound ? 'text-blue-700' : 'text-muted-foreground'}
                    >
                      [{labelText}]
                    </span>
                    {msg.messageType ? (
                      <span className="text-muted-foreground">
                        {msg.messageType.toLowerCase()}
                      </span>
                    ) : null}
                    {msg.senderName ? (
                      <span className="text-muted-foreground normal-case font-normal">
                        {msg.senderName}
                      </span>
                    ) : null}
                    {msg.dateAdded ? (
                      <time
                        className="ml-auto text-muted-foreground normal-case font-normal"
                        dateTime={msg.dateAdded}
                        title={new Date(msg.dateAdded).toLocaleString()}
                      >
                        {new Date(msg.dateAdded).toLocaleString()}
                      </time>
                    ) : null}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm">
                    {msg.body ?? <span className="text-muted-foreground italic">(no body)</span>}
                  </p>
                </li>
              )
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
